import {
  type FormFieldDescriptor,
  type NarrowFieldsByValueType,
  type TidalFormBridgeOptions,
  type TidalFormState,
  useTidalFormBridge,
} from '@workwave-tidal/core/form'

import { FormHelperText, Stack } from '@mui/material'

import type { BulkFieldValue } from '../../../types'
import { useActions } from './hooks/useActions'

import { Calendar } from './components/Calendar'
import { EligibilityTypePicker } from './components/EligibilityTypePicker'

export type BulkEligibilityFieldProps = {
  label: string
  helperText?: string
  testId?: string
  disabled?: boolean
}

export type InputValue = BulkFieldValue<uui.domain.client.rm.Eligibility>

export type InputProps<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = InputValue,
> = {
  bulkEligibilityFieldProps: BulkEligibilityFieldProps
} & TidalFormBridgeOptions<InputValue, TargetField, State, AcceptedFormValue>

export function BulkEligibility<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = InputValue,
>(props: InputProps<TargetField, State, AcceptedFormValue>) {
  const {
    bulkEligibilityFieldProps: { testId, label, disabled },
    initialValue,
    validateOn = 'change',
    ...bridgeOptions
  } = props

  const {
    fieldInfo: {
      value: fieldValue,
      showErrorAccent,
      fieldHasError,
      fieldDisabled,
      errorOrHelperText,
      field: { visible },
      onChange,
    },
  } = useTidalFormBridge<InputValue, TargetField, State, AcceptedFormValue>({
    ...bridgeOptions,
    validateOn,
    initialValue,
  })

  const actions = useActions(onChange)

  const typePickerValue = fieldValue.status === 'mixed' ? 'mixed' : fieldValue.value.type

  if (!visible) return null

  return (
    <Stack spacing={2} data-testid={testId} data-trackid={testId}>
      <EligibilityTypePicker
        label={label}
        value={typePickerValue}
        onChange={actions.onChangeEligibilityType}
        disabled={fieldDisabled || disabled}
        error={fieldHasError}
      />
      {fieldValue.status === 'exact' && (
        <Calendar eligibility={fieldValue.value} onChange={actions.onChangeDate} />
      )}
      {showErrorAccent && fieldHasError && (
        <FormHelperText error={fieldHasError}>{errorOrHelperText}</FormHelperText>
      )}
    </Stack>
  )
}
